/**
 * Build browser download names for ad resources, e.g.
 * {@code 260820-vintage-zenith-console-and-color-bars.jpeg}.
 */

function pad2(n) {
    return String(n).padStart(2, "0");
}

/** @param {number|string|Date|null|undefined} createdAt epoch ms, Date, or parseable string */
export function formatDownloadDatePrefix(createdAt) {
    let d;
    if (createdAt instanceof Date) {
        d = createdAt;
    } else if (typeof createdAt === "number" && Number.isFinite(createdAt) && createdAt > 0) {
        d = new Date(createdAt);
    } else if (typeof createdAt === "string" && createdAt.trim()) {
        const n = Number(createdAt);
        d = Number.isFinite(n) && n > 0 ? new Date(n) : new Date(createdAt);
    } else {
        d = new Date();
    }
    if (Number.isNaN(d.getTime())) {
        d = new Date();
    }
    const yy = String(d.getFullYear()).slice(-2);
    return `${yy}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`;
}

/** @param {string|null|undefined} title */
export function slugifyAdTitle(title) {
    const raw = typeof title === "string" ? title.trim().toLowerCase() : "";
    const slug = raw
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .replace(/-{2,}/g, "-");
    return slug || "ad-image";
}

/**
 * @param {{ contentType?: string, content_type?: string, s3Key?: string, s3_key?: string, videoUrl?: string, video_url?: string } | null | undefined} row
 * @param {boolean} [isVideo]
 */
export function extensionForAdResource(row, isVideo = false) {
    if (isVideo) return ".mp4";
    const mime = String(row?.contentType ?? row?.content_type ?? "").toLowerCase();
    if (mime.includes("jpeg") || mime === "image/jpg") return ".jpeg";
    if (mime.includes("png")) return ".png";
    if (mime.includes("webp")) return ".webp";
    if (mime.includes("mp4") || mime.includes("video/")) return ".mp4";
    const key = String(row?.s3Key ?? row?.s3_key ?? row?.videoUrl ?? row?.video_url ?? "");
    const match = key.split("?")[0].match(/\.([a-z0-9]+)$/i);
    if (match?.[1]) {
        const ext = match[1].toLowerCase();
        if (ext === "jpg") return ".jpeg";
        return `.${ext}`;
    }
    return ".png";
}

/**
 * Prefer human title from {@code prompt} when it is not a Bedrock/crop system string.
 * @param {{ prompt?: string, s3Key?: string, s3_key?: string, resourceType?: string } | null | undefined} row
 */
export function titleForAdDownload(row) {
    const prompt = typeof row?.prompt === "string" ? row.prompt.trim() : "";
    const resourceType = String(row?.resourceType ?? row?.resource_type ?? "").toUpperCase();
    if (prompt) {
        const lower = prompt.toLowerCase();
        const looksSystem = lower.startsWith("cropped from story ");
        if (!looksSystem && (resourceType === "IPHONE_BACKGROUND" || resourceType === "PC_BACKGROUND" || prompt.length <= 120)) {
            return prompt;
        }
        if (!looksSystem && prompt.length > 80) {
            const first = prompt.split(/[.!?\n]/)[0]?.trim() || prompt;
            if (first.length >= 8 && first.length <= 100) return first;
        }
        if (!looksSystem) {
            return prompt.length > 100 ? prompt.slice(0, 100) : prompt;
        }
    }
    const key = String(row?.s3Key ?? row?.s3_key ?? "");
    const base = key.includes("/") ? key.split("/").pop() : key;
    if (base) {
        return base.replace(/\.[a-z0-9]+$/i, "");
    }
    return resourceType || "ad-image";
}

/**
 * @param {object|null|undefined} row
 * @param {boolean} [isVideo]
 */
export function downloadFileNameForAdResource(row, isVideo = false) {
    const date = formatDownloadDatePrefix(row?.createdAt ?? row?.created_at);
    const slug = slugifyAdTitle(titleForAdDownload(row));
    const ext = extensionForAdResource(row, isVideo);
    return `${date}-${slug}${ext}`;
}

export function sanitizeDownloadFileName(name, fallback) {
    const cleaned = String(name ?? "")
        .trim()
        .replace(/[<>:"/\\|?*\u0000-\u001f]/g, "_")
        .replace(/\s+/g, " ");
    if (!cleaned || cleaned === "." || cleaned === "..") {
        return fallback;
    }
    const fallbackExt = fallback.includes(".") ? fallback.slice(fallback.lastIndexOf(".")) : "";
    if (fallbackExt && !cleaned.toLowerCase().endsWith(fallbackExt.toLowerCase())) {
        return `${cleaned}${fallbackExt}`;
    }
    return cleaned;
}
